import cloneDeep from "lodash/cloneDeep";
import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppStore } from "@/app/hooks";
import { legacyAction } from "@/app/legacySlice";
import { resolveRoute } from "@/app/routes";
import { markSaved } from "@/features/configuration/slice";
import { configurationSaved } from "@/features/shell/actions";
import {
  useUpdateShopConfigurationMutation,
  useUpdateShopDataMutation,
  useUpdateShopLanguageMutation,
} from "@/features/shell/api";
import { selectCurrentConfiguration } from "@/features/shell/legacySelectors";
import type { Shop } from "@/shared/types/shop";
import { useToast } from "@/shared/ui";

const setWebsites = legacyAction<Shop[]>("setWebsites");

/** Chiave della configurazione da inviare oltre a `setup`, in base alla sezione corrente (SaveChanges.saveChanges). */
function sectionKey(pathname: string): "setup" | "fineTuning" | "layout" | "" {
  const group = resolveRoute(pathname).group;
  if (group === "setup") return "setup";
  if (group === "fineTuning") return "fineTuning";
  if (group === "layoutDesign") return "layout";
  return "";
}

export function useSaveConfiguration() {
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const toast = useToast();
  const [updateLanguage] = useUpdateShopLanguageMutation();
  const [updateData] = useUpdateShopDataMutation();
  const [updateConfiguration] = useUpdateShopConfigurationMutation();
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    const configuration = selectCurrentConfiguration(store.getState()) as Shop & Record<string, unknown>;
    const key = sectionKey(location.pathname);
    const body: Record<string, unknown> = {};
    if (key) body[key] = cloneDeep(configuration[key]);
    const setup = cloneDeep((configuration.setup ?? {}) as Record<string, unknown>);
    body.setup = setup;
    setSaving(true);
    try {
      if (key === "setup") {
        if (setup.language_iso) {
          await updateLanguage({ id: configuration.id, iso6391: String(setup.language_iso) }).unwrap();
          toast.warning("Custom import code changed!", { duration: 2000 });
          delete setup.language_iso;
        }
        if (setup.feedUrl) {
          const data = cloneDeep((configuration.cms_data ?? {}) as Record<string, unknown>);
          data.feedUrl = setup.feedUrl;
          await updateData({ id: configuration.id, data }).unwrap();
          delete setup.feedUrl;
        }
      }
      const shops = await updateConfiguration({ id: configuration.id, body }).unwrap();
      dispatch(setWebsites(shops));
      dispatch(markSaved());
      dispatch(configurationSaved());
    } finally {
      setSaving(false);
    }
  }, [dispatch, location.pathname, store, toast, updateConfiguration, updateData, updateLanguage]);

  return { save, saving };
}
